import React from 'react';
import {
  BusinessTitlesUtil,
  CondensedHeader,
  Gutter,
  Layout,
  TaxonomyUtil,
  Well,
} from '@axiom/ui';
import type { Candidate, CandidateRedux, Taxonomy } from '@axiom/validation';
import { CountryUtil, CurrencyUtil } from '@axiom/utils';
import { CandidatesConst, type CurrencyCodeValueTypes } from '@axiom/const';

import { formatPercentage } from '../../utils/parsers-util';

import { TalentProfileDetailCompensationDataRow } from './TalentProfileDetailCompensationDataRow';
import { TalentProfileDetailCompensationUtil } from './talent-profile-detail-compensation-util';

export interface TalentProfileDetailCompensationSectionProps {
  talent: CandidateRedux | Candidate;
  currencyCode: CurrencyCodeValueTypes;
  taxonomy: Taxonomy;
}

export const TalentProfileDetailCompensationSection: React.FC<
  TalentProfileDetailCompensationSectionProps
> = ({ talent, currencyCode, taxonomy }) => {
  const shouldShowClientRates =
    talent.addressCountry &&
    !CountryUtil.isNoAdHocCountry(talent.addressCountry);
  const businessTitle =
    TaxonomyUtil.getBusinessTitle(taxonomy, talent.businessTitle)
      ?.displayName ?? null;
  const baseLegalRole = BusinessTitlesUtil.locationConversion({
    businessTitle,
    candidate: talent,
  });
  const isContractor =
    talent.employeeType === CandidatesConst.EmployeeTypes.Contractor;

  return (
    <>
      <Well background="light">
        <Layout direction="vertical" verticalGutter="4px">
          <TalentProfileDetailCompensationDataRow
            label="Legal role"
            value={baseLegalRole}
          />
          <TalentProfileDetailCompensationDataRow
            label="Employee type"
            value={talent.employeeType}
            showWarning={isContractor}
          />
          <TalentProfileDetailCompensationDataRow
            label="Rewards Plan"
            value={talent.rewardsPlan}
          />
        </Layout>
      </Well>
      <Gutter bottom="16px" />
      <Well background="light">
        <CondensedHeader name="TALENT_COMPENSATION_SECTION">
          Talent compensation ({talent.currencyType})
        </CondensedHeader>
        <Gutter bottom="8px" />
        <Layout direction="vertical" verticalGutter="4px">
          <TalentProfileDetailCompensationDataRow
            label="Base annual salary"
            value={CurrencyUtil.getFormattedCurrency({
              value: talent.baseAnnualSalary,
              currencyCode,
            })}
          />
          <TalentProfileDetailCompensationDataRow
            label="Target bonus"
            value={formatPercentage(talent.bonusPercentage)}
          />
          <TalentProfileDetailCompensationDataRow
            label="Total annual compensation"
            value={CurrencyUtil.getFormattedCurrency({
              value: talent.compensation,
              currencyCode,
            })}
          />
          <TalentProfileDetailCompensationDataRow
            label="Standard hourly comp"
            value={CurrencyUtil.getFormattedCurrency({
              value: talent.hourlyCompensation,
              currencyCode,
            })}
          />
          <TalentProfileDetailCompensationDataRow
            label="Premium hourly comp"
            value={CurrencyUtil.getFormattedCurrency({
              value: talent.compHourlyPremium,
              currencyCode,
            })}
          />
          <TalentProfileDetailCompensationDataRow
            label="40 hour (full time) hourly comp"
            value={CurrencyUtil.getFormattedCurrency({
              value: talent.compHourly40hr,
              currencyCode,
            })}
          />
        </Layout>
      </Well>
      <Gutter bottom="16px" />
      {shouldShowClientRates ? (
        <Well background="light" name="CLIENT_RATES_SECTION">
          <CondensedHeader name="CLIENT_RATES_HEADER">
            Client rates ({talent.currencyType})
          </CondensedHeader>
          <Gutter bottom="8px" />
          <Layout direction="vertical" verticalGutter="4px">
            <TalentProfileDetailCompensationDataRow
              label="Estimated hourly rate"
              value={TalentProfileDetailCompensationUtil.calculateEstimatedHourlyRate(
                talent,
                currencyCode
              )}
            />
            <TalentProfileDetailCompensationDataRow
              label="Estimated daily rate"
              value={TalentProfileDetailCompensationUtil.calculateEstimatedDailyRate(
                talent,
                currencyCode
              )}
            />
            <TalentProfileDetailCompensationDataRow
              label="Full time weekly rate"
              value={CurrencyUtil.getFormattedCurrency({
                value: talent.displayWeeklyRate,
                currencyCode,
              })}
            />
            <TalentProfileDetailCompensationDataRow
              label="Estimated monthly rate"
              value={TalentProfileDetailCompensationUtil.calculateEstimatedMonthlyRate(
                talent,
                currencyCode
              )}
            />
          </Layout>
        </Well>
      ) : null}
    </>
  );
};
